import {Descriptions, message, Modal, Spin, Typography} from "antd";
import {useCallback, useEffect, useRef} from "react";
import {matchPath, useNavigate} from "react-router-dom";
import {
    ProductApi,
    ProductDetailsDto,
    ProductMinimalDto,
    StockApi,
    StockDto,
    StockOperationApi,
    StockOperationDetailsDto,
    WarehouseApi,
    WarehouseDetailsDto
} from "../api";
import {warehouseTypeTranslations} from "../components/warehouseTypeSelect/WarehouseTypeSelect";
import {RoutingPaths} from "../pages/routing/routingContainer";
import ApiService from "../services/apiService";
import productTypeTranslationsHelper from "../helpers/productTypeHelper";
import {stockOperationStatusTranslationHelper} from "../helpers/stockOperationStatusHelper";
import stockOperationTypeTranslationHelper from "../helpers/stockOperationTypeHelper";

type ScannerHookOptions = {
    prefix?: string;
    suffix?: string;
    onScan?: (data: string) => void;
    scanTimeout?: number;
    apiService?: ApiService | null;
};

type ScannerPreviewField = {
    label: string;
    value: string;
};

export type ResolvedScannerTarget =
    | {
    rawData: string;
    path: string;
    kind: "warehouse";
    params: {warehouseId: number};
}
    | {
    rawData: string;
    path: string;
    kind: "product";
    params: {productId: number};
}
    | {
    rawData: string;
    path: string;
    kind: "productBatch";
    params: {productId: number; batchNumber: string};
}
    | {
    rawData: string;
    path: string;
    kind: "productBatchUnit";
    params: {productId: number; batchNumber: string; unitId: number};
}
    | {
    rawData: string;
    path: string;
    kind: "stockOperation";
    params: {stockOperationId: number};
}
    | {
    rawData: string;
    path: string;
    kind: "unknown";
    params: Record<string, never>;
};

type ScannerPreviewState = {
    isLoading: boolean;
    title?: string;
    subtitle?: string;
    detailsPath?: string;
    fields: ScannerPreviewField[];
};

const defaultPreviewState: ScannerPreviewState = {
    isLoading: false,
    fields: []
};

const scannerCustomHandlers = new Set<(target: ResolvedScannerTarget) => boolean | Promise<boolean>>();

export function useScannerListener({
    prefix = "SCAN:",
    suffix = ":SCAN",
    onScan,
    scanTimeout = 1000,
    apiService
}: ScannerHookOptions) {
    const bufferRef = useRef<string>("");
    const lastKeyTimeRef = useRef<number>(0);
    const navigate = useNavigate();
    const [modal, contextHolder] = Modal.useModal();
    const previewRef = useRef<ScannerPreviewState>(defaultPreviewState);
    const modalRef = useRef<ReturnType<typeof modal.confirm> | null>(null);

    const destroyPreviewModal = useCallback(() => {
        modalRef.current?.destroy();
        modalRef.current = null;
        previewRef.current = defaultPreviewState;
    }, []);

    const openOrUpdatePreviewModal = useCallback((previewState: ScannerPreviewState) => {
        previewRef.current = previewState;

        const modalConfig = {
            title: previewState.title || "Zeskanowany kod",
            icon: null,
            okText: "Przejdź do szczegółów",
            cancelText: "Zamknij",
            okCancel: true,
            okButtonProps: {
                disabled: previewState.isLoading || !previewState.detailsPath
            },
            content: renderPreviewContent(previewState),
            onOk: () => {
                if (previewRef.current.detailsPath) {
                    navigate(previewRef.current.detailsPath);
                }
                destroyPreviewModal();
            },
            onCancel: () => destroyPreviewModal()
        };

        if (modalRef.current) {
            modalRef.current.update(modalConfig);
            return;
        }

        modalRef.current = modal.confirm(modalConfig);
    }, [destroyPreviewModal, modal, navigate]);

    const handleScanData = useCallback(async (data: string) => {
        if (onScan) {
            const resolvedTarget = resolveScanNavigationTarget(data);

            if (resolvedTarget?.type === "internal") {
                navigate(resolvedTarget.path);
                return;
            }

            if (resolvedTarget?.type === "external") {
                window.location.href = resolvedTarget.url;
                return;
            }

            await onScan(data);
            return;
        }

        const resolvedTarget = resolveWhitelistedInternalTarget(data);
        if (!resolvedTarget) {
            message.warning("Zeskanowany kod nie jest obsługiwany przez podgląd skanera.");
            return;
        }

        const scannerTarget = parseScannerTarget(resolvedTarget.path, data);

        for (const handler of Array.from(scannerCustomHandlers).reverse()) {
            const isHandled = await handler(scannerTarget);
            if (isHandled) {
                destroyPreviewModal();
                return;
            }
        }

        if (!apiService) {
            message.warning("Podgląd skanera jest jeszcze niedostępny. Spróbuj ponownie za chwilę.");
            return;
        }

        openOrUpdatePreviewModal({
            isLoading: true,
            title: "Ładowanie danych...",
            subtitle: resolvedTarget.path,
            detailsPath: resolvedTarget.path,
            fields: []
        });

        const preview = await loadPreviewForPath(resolvedTarget.path, apiService);
        if (!preview) {
            destroyPreviewModal();
            message.warning("Nie udało się rozpoznać encji z zeskanowanego kodu.");
            return;
        }

        openOrUpdatePreviewModal({
            isLoading: false,
            ...preview
        });
    }, [apiService, destroyPreviewModal, navigate, onScan, openOrUpdatePreviewModal]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            const now = Date.now();

            if (now - lastKeyTimeRef.current > scanTimeout) {
                bufferRef.current = "";
            }
            lastKeyTimeRef.current = now;

            if (typeof e.key === "string" && e.key.length === 1) {
                bufferRef.current += e.key;
            }

            if (bufferRef.current.startsWith(prefix) && bufferRef.current.endsWith(suffix)) {
                const data = bufferRef.current.slice(prefix.length, -suffix.length);
                void handleScanData(data);
                bufferRef.current = "";
            }

            if (bufferRef.current.length > 300) {
                bufferRef.current = "";
            }
        };

        window.addEventListener("keydown", handleKey);

        return () => window.removeEventListener("keydown", handleKey);
    }, [handleScanData, prefix, scanTimeout, suffix]);

    useEffect(() => () => destroyPreviewModal(), []);

    return {
        scannerModalContextHolder: contextHolder,
        handleScanData
    };
}

export function useRegisterScannerHandler(handler: (target: ResolvedScannerTarget) => boolean | Promise<boolean>) {
    useEffect(() => {
        scannerCustomHandlers.add(handler);

        return () => {
            scannerCustomHandlers.delete(handler);
        };
    }, [handler]);
}

function renderPreviewContent(previewState: ScannerPreviewState) {
    if (previewState.isLoading) {
        return (
            <div style={{display: "flex", justifyContent: "center", padding: "24px 0"}}>
                <Spin />
            </div>
        );
    }

    return (
        <>
            {previewState.subtitle && (
                <Typography.Text type="secondary" style={{display: "block", marginBottom: 16}}>
                    {previewState.subtitle}
                </Typography.Text>
            )}
            <Descriptions column={1} size="small">
                {previewState.fields.map(field => (
                    <Descriptions.Item key={field.label} label={field.label}>
                        {field.value}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        </>
    );
}

function resolveScanNavigationTarget(data: string) {
    const appUrl = process.env.REACT_APP_URL;

    if (appUrl && data.startsWith(appUrl)) {
        return {
            type: "internal" as const,
            path: data.slice(appUrl.length)
        };
    }

    if (data.startsWith("/")) {
        return {
            type: "internal" as const,
            path: data
        };
    }

    if (data.startsWith("http://") || data.startsWith("https://")) {
        return {
            type: "external" as const,
            url: data
        };
    }

    return null;
}

function resolveWhitelistedInternalTarget(data: string) {
    if (data.startsWith("/")) {
        return {path: data};
    }

    if (!data.startsWith("http://") && !data.startsWith("https://")) {
        return null;
    }

    try {
        const url = new URL(data);
        const whitelist = getScannerOriginWhitelist();

        if (!whitelist.has(url.origin)) {
            return null;
        }

        return {
            path: `${url.pathname}${url.search}${url.hash}`
        };
    } catch {
        return null;
    }
}

function getScannerOriginWhitelist() {
    const whitelist = new Set<string>();
    const envWhitelist = process.env.REACT_APP_SCANNER_URL_WHITELIST
        ?.split(",")
        .map(item => item.trim())
        .filter(Boolean) || [];

    whitelist.add(window.location.origin);

    if (process.env.REACT_APP_URL) {
        try {
            whitelist.add(new URL(process.env.REACT_APP_URL).origin);
        } catch {
        }
    }

    for (const item of envWhitelist) {
        try {
            whitelist.add(new URL(item).origin);
        } catch {
            whitelist.add(item);
        }
    }

    return whitelist;
}

async function loadPreviewForPath(path: string, apiService: ApiService) {
    const warehouseMatch = matchPath(RoutingPaths.warehouseStockTable.route, path);
    if (warehouseMatch?.params.warehouseId) {
        const warehouseId = Number(warehouseMatch.params.warehouseId);
        const warehouseResponse = await apiService.getApi(WarehouseApi).apiWarehouseIdGet(warehouseId);
        return buildWarehousePreview(path, warehouseResponse.data);
    }

    const productBatchUnitMatch = matchPath(RoutingPaths.productBatchUnitStockScan.route, path);
    if (productBatchUnitMatch?.params.productId && productBatchUnitMatch.params.batchNumber && productBatchUnitMatch.params.unitId) {
        const productId = Number(productBatchUnitMatch.params.productId);
        const batchNumber = decodeURIComponent(productBatchUnitMatch.params.batchNumber);
        const unitId = Number(productBatchUnitMatch.params.unitId);
        const [productResponse, batchResponse] = await Promise.all([
            apiService.getApi(ProductApi).apiProductIdMinimalGet(productId),
            apiService.getApi(StockApi).apiStockStockProductProductIdBatchBatchNumberGet(productId, batchNumber)
        ]);
        return buildProductBatchUnitPreview(path, productResponse.data, batchNumber, unitId, batchResponse.data);
    }

    const productBatchMatch = matchPath(RoutingPaths.productBatchStockTable.route, path);
    if (productBatchMatch?.params.productId && productBatchMatch.params.batchNumber) {
        const productId = Number(productBatchMatch.params.productId);
        const batchNumber = decodeURIComponent(productBatchMatch.params.batchNumber);
        const [productResponse, batchResponse] = await Promise.all([
            apiService.getApi(ProductApi).apiProductIdMinimalGet(productId),
            apiService.getApi(StockApi).apiStockStockProductProductIdBatchBatchNumberGet(productId, batchNumber)
        ]);
        return buildProductBatchPreview(path, productResponse.data, batchNumber, batchResponse.data);
    }

    const productMatch = matchPath(RoutingPaths.productStockTable.route, path);
    if (productMatch?.params.productId) {
        const productId = Number(productMatch.params.productId);
        const productResponse = await apiService.getApi(ProductApi).apiProductIdGet(productId);
        return buildProductPreview(path, productResponse.data);
    }

    const stockOperationPreview = await loadStockOperationPreview(path, apiService);
    if (stockOperationPreview) {
        return stockOperationPreview;
    }

    return null;
}

function parseScannerTarget(path: string, rawData: string): ResolvedScannerTarget {
    const warehouseMatch = matchPath(RoutingPaths.warehouseStockTable.route, path);
    if (warehouseMatch?.params.warehouseId) {
        return {
            rawData,
            path,
            kind: "warehouse",
            params: {warehouseId: Number(warehouseMatch.params.warehouseId)}
        };
    }

    const productBatchUnitMatch = matchPath(RoutingPaths.productBatchUnitStockScan.route, path);
    if (productBatchUnitMatch?.params.productId && productBatchUnitMatch.params.batchNumber && productBatchUnitMatch.params.unitId) {
        return {
            rawData,
            path,
            kind: "productBatchUnit",
            params: {
                productId: Number(productBatchUnitMatch.params.productId),
                batchNumber: decodeURIComponent(productBatchUnitMatch.params.batchNumber),
                unitId: Number(productBatchUnitMatch.params.unitId)
            }
        };
    }

    const productBatchMatch = matchPath(RoutingPaths.productBatchStockTable.route, path);
    if (productBatchMatch?.params.productId && productBatchMatch.params.batchNumber) {
        return {
            rawData,
            path,
            kind: "productBatch",
            params: {
                productId: Number(productBatchMatch.params.productId),
                batchNumber: decodeURIComponent(productBatchMatch.params.batchNumber)
            }
        };
    }

    const productMatch = matchPath(RoutingPaths.productStockTable.route, path);
    if (productMatch?.params.productId) {
        return {
            rawData,
            path,
            kind: "product",
            params: {productId: Number(productMatch.params.productId)}
        };
    }

    for (const route of [
        RoutingPaths.productionStockOperationDetailsTable.route,
        RoutingPaths.internalStockOperationDetailsTable.route,
        RoutingPaths.stockOperationDetailsTable.route
    ]) {
        const stockOperationMatch = matchPath(route, path);
        if (stockOperationMatch?.params.stockOperationId) {
            return {
                rawData,
                path,
                kind: "stockOperation",
                params: {stockOperationId: Number(stockOperationMatch.params.stockOperationId)}
            };
        }
    }

    return {
        rawData,
        path,
        kind: "unknown",
        params: {}
    };
}

async function loadStockOperationPreview(path: string, apiService: ApiService) {
    const routes = [
        RoutingPaths.productionStockOperationDetailsTable.route,
        RoutingPaths.internalStockOperationDetailsTable.route,
        RoutingPaths.stockOperationDetailsTable.route
    ];

    for (const route of routes) {
        const match = matchPath(route, path);
        if (!match?.params.stockOperationId) {
            continue;
        }

        const stockOperationId = Number(match.params.stockOperationId);
        const response = await apiService.getApi(StockOperationApi).apiStockOperationIdGet(stockOperationId);
        return buildStockOperationPreview(path, response.data);
    }

    return null;
}

function buildWarehousePreview(path: string, warehouse: WarehouseDetailsDto) {
    return {
        title: warehouse.name || `Magazyn #${warehouse.id}`,
        subtitle: "Magazyn",
        detailsPath: path,
        fields: [
            createField("ID", warehouse.id),
            createField("Typ", warehouse.type ? warehouseTypeTranslations[warehouse.type] || warehouse.type : warehouse.type),
            createField("Magazyn nadrzędny", warehouse.parentWarehouse?.name || warehouse.parentWarehouseId),
            createField("Opis", warehouse.description)
        ].filter(isFieldDefined)
    };
}

function buildProductPreview(path: string, product: ProductDetailsDto) {
    return {
        title: product.name || `Produkt #${product.id}`,
        subtitle: "Produkt",
        detailsPath: path,
        fields: [
            createField("ID", product.id),
            createField("Typ", product.type ? productTypeTranslationsHelper(product.type) || product.type : product.type),
            createField("Jednostka bazowa", product.baseUnit?.name),
            createField("Opis", product.description)
        ].filter(isFieldDefined)
    };
}

function buildProductBatchPreview(path: string, product: ProductMinimalDto, batchNumber: string, batchStock: StockDto[]) {
    const totalQuantity = batchStock.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const firstUnit = batchStock.find(item => item.unit?.name)?.unit?.name;

    return {
        title: product.name || `Produkt #${product.id}`,
        subtitle: "Produkt + numer serii",
        detailsPath: path,
        fields: [
            createField("ID produktu", product.id),
            createField("Numer serii", batchNumber),
            createField("Liczba pozycji", batchStock.length),
            createField("Suma ilości", firstUnit ? `${totalQuantity} ${firstUnit}` : totalQuantity)
        ].filter(isFieldDefined)
    };
}

function buildProductBatchUnitPreview(path: string, product: ProductMinimalDto, batchNumber: string, unitId: number, batchStock: StockDto[]) {
    const stockItem = batchStock.find(item => item.unitId === unitId);
    const detailsPath = path.replace(/\/unit\/\d+$/, "");

    return {
        title: product.name || `Produkt #${product.id}`,
        subtitle: "Produkt + numer serii + jednostka",
        detailsPath,
        fields: [
            createField("ID produktu", product.id),
            createField("Numer serii", batchNumber),
            createField("Jednostka", stockItem?.unit?.name || unitId),
            createField("Ilość", stockItem?.quantity)
        ].filter(isFieldDefined)
    };
}

function buildStockOperationPreview(path: string, operation: StockOperationDetailsDto) {
    return {
        title: operation.documentNumber || `Operacja #${operation.id}`,
        subtitle: "Operacja",
        detailsPath: path,
        fields: [
            createField("ID", operation.id),
            createField("Typ", operation.type ? stockOperationTypeTranslationHelper(operation.type) || operation.type : operation.type),
            createField("Status", operation.status ? stockOperationStatusTranslationHelper(operation.status) || operation.status : operation.status),
            createField("Data", operation.date),
            createField("Magazyn źródłowy", operation.warehouse?.name),
            createField("Magazyn docelowy", operation.destinationWarehouse?.name)
        ].filter(isFieldDefined)
    };
}

function createField(label: string, value: string | number | null | undefined) {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    return {
        label,
        value: String(value)
    };
}

function isFieldDefined(field: ScannerPreviewField | null): field is ScannerPreviewField {
    return Boolean(field);
}
